import { FormsModule } from '@angular/forms';
import { Component, OnInit, Input, Output, EventEmitter, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LTPrimeNgModule } from 'src/app/third-party/lt-primeng.module';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { ExportOptions } from '../../services/papa-parse-export-service.service';
import { ExportStats } from '../../classes/export-manager.facade';
import { MatDividerModule } from '@angular/material/divider';

@Component({
  selector: 'app-lt-export-controls',
  standalone: true,
  imports: [FormsModule, CommonModule, LTPrimeNgModule, MatIconModule, MatButtonModule, MatCardModule, MatDividerModule],
  templateUrl: './lt-export-controls.component.html',
  styleUrl: './lt-export-controls.component.scss',
})
export class LtExportControlsComponent implements OnInit {
  @Input() exportStats!: ExportStats;
  @Input() exportOptions!: ExportOptions;

  @Output() optionsChange = new EventEmitter<Partial<ExportOptions>>();
  @Output() scopeChange = new EventEmitter<'all' | 'filtered' | 'selected'>();
  @Output() exportRequested = new EventEmitter<'all' | 'filtered' | 'selected'>();

  selectedScope = signal<'all' | 'filtered' | 'selected'>('all');
  includeAggregatesValue = signal<boolean>(false);
  aggregatePositionValue = signal<'top' | 'bottom'>('bottom');
  separatorRowValue = true;

  aggregatePositionOptions = [
    { label: 'Top', value: 'top', icon: 'pi pi-arrow-up' },
    { label: 'Bottom', value: 'bottom', icon: 'pi pi-arrow-down' },
  ];

  ngOnInit(): void {
    // Initialize from input options
    if (this.exportOptions) {
      this.includeAggregatesValue.set(this.exportOptions.includeAggregates || false);
      this.aggregatePositionValue.set(this.exportOptions.aggregatePosition || 'bottom');
      this.separatorRowValue = this.exportOptions.aggregateSeparatorRow !== false;
    }

    // Auto-select best scope
    this.autoSelectScope();
  }

  private autoSelectScope(): void {
    if (this.exportStats.selectedRecords > 0) {
      this.selectScope('selected');
    } else if (this.exportStats.hasActiveFilters && this.exportStats.filteredRecords > 0) {
      this.selectScope('filtered');
    } else {
      this.selectScope('all');
    }
  }

  selectScope(scope: 'all' | 'filtered' | 'selected'): void {
    // Check if scope is valid
    const canSelect = this.canSelectScope(scope);
    if (!canSelect) return;

    this.selectedScope.set(scope);
    this.scopeChange.emit(scope);
  }

  private canSelectScope(scope: 'all' | 'filtered' | 'selected'): boolean {
    switch (scope) {
      case 'selected':
        return this.exportStats.canExportSelected;
      case 'filtered':
        return this.exportStats.canExportFiltered;
      case 'all':
      default:
        return this.exportStats.canExportAll;
    }
  }

  onAggregateToggle(event: any): void {
    this.includeAggregatesValue.set(event.checked);
    this.optionsChange.emit({ includeAggregates: event.checked });
  }

  onAggregatePositionChange(event: any): void {
    this.aggregatePositionValue.set(event.value);
    this.optionsChange.emit({ aggregatePosition: event.value });
  }

  onSeparatorRowToggle(event: any): void {
    this.separatorRowValue = event.checked;
    this.optionsChange.emit({ aggregateSeparatorRow: event.checked });
  }

  quickExport(): void {
    if (!this.canQuickExport()) return;
    this.exportRequested.emit(this.selectedScope());
  }

  canQuickExport(): boolean {
    const scope = this.selectedScope();
    switch (scope) {
      case 'selected':
        return this.exportStats.selectedRecords > 0;
      case 'filtered':
        return this.exportStats.hasActiveFilters;
      case 'all':
      default:
        return this.exportStats.totalRecords > 0;
    }
  }

  getExportRecordCount(): number {
    const scope = this.selectedScope();
    switch (scope) {
      case 'selected':
        return this.exportStats.selectedRecords;
      case 'filtered':
        return this.exportStats.filteredRecords;
      case 'all':
      default:
        return this.exportStats.totalRecords;
    }
  }

}
